<template>
  <OmtGrid :class="{ 'audit-details': hasAuditRefund }">
    <OmtRow v-if="refundDetails.protocol">
      <OmtTypography
        as="h1-bold"
        mb="24px"
      >
        Protocolo {{ refundDetails.protocol }}
      </OmtTypography>
    </OmtRow>

    <OmtRow
      mb="16px"
      class="audit-details__summary"
    >
      <CFlex
        width="100%"
        flexDirection="row"
        gap="16px"
      >
        <CBox flex="1">
          <TextTile v-bind="{ title: 'Data da solicitação', description: refundDetails.createdAt }" />
        </CBox>

        <CBox
          v-if="refundDetails.showValues"
          flex="1"
        >
          <TextTile
            v-bind="{
              title: hasAuditRefund ? 'Valor total' : 'Valor total apresentado',
              description: refundDetails.totalAmount
            }"
          />
        </CBox>
      </CFlex>
    </OmtRow>

    <OmtRow
      v-if="isCreditReleased"
      mb="16px"
    >
      <CBox flex="1">
        <TextTile v-bind="{ title: 'Valor total reembolsado', description: refundDetails.refundedTotal }" />
      </CBox>
    </OmtRow>

    <template v-if="hasDemonstrativo">
      <OmtButton
        mb="24px"
        height="40px"
        width="223px"
        @click="downloadDemonstrativo"
      >
        <OmtIcons
          size="1x"
          name="arrow-down-to-bracket"
          color="white"
          mr="8px"
        />
        <OmtTypography
          as="h3-bold"
          color="#FFFFFF"
          >Detalhes do pagamento</OmtTypography
        >
      </OmtButton>
    </template>

    <OmtRow
      mb="24px"
      class="audit-details__account"
    >
      <CBox class="refund-detail-section">
        <OmtTypography
          as="h2-bold"
          mb="16px"
        >
          Conta Corrente para Crédito
        </OmtTypography>

        <CFlex
          class="audit-details__account-fields"
          flex-wrap="wrap"
          gap="16px 64px"
        >
          <CBox :w="['100%', '220px']">
            <TextTile v-bind="{ title: 'Favorecido', description: refundDetails.accountDetails.name }" />
          </CBox>

          <CBox :w="['calc(50% - 8px)', '120px']">
            <TextTile v-bind="{ title: 'Conta Corrente', description: refundDetails.accountDetails.account }" />
          </CBox>

          <CBox :w="['calc(50% - 8px)', '120px']">
            <TextTile v-bind="{ title: 'Agência', description: refundDetails.accountDetails.agency }" />
          </CBox>

          <CBox :w="['100%', '220px']">
            <TextTile v-bind="{ title: 'Banco', description: refundDetails.accountDetails.bank }" />
          </CBox>
        </CFlex>
      </CBox>
    </OmtRow>

    <OmtRow
      mb="24px"
      class="audit-details__notes"
    >
      <CBox class="refund-detail-section">
        <OmtTypography
          as="h2-bold"
          mb="16px"
        >
          Observações
        </OmtTypography>

        <template v-if="refundDetails.obsHistory && refundDetails.obsHistory.length">
          <CBox
            v-for="(obs, index) in refundDetails.obsHistory"
            :key="`obs-${index}`"
          >
            <CFlex
              align-items="center"
              justify-content="space-between"
              mb="8px"
            >
              <OmtTypography
                v-if="obs.Data"
                as="p2"
                color="#8C8C8C"
                >{{ obs.Data }}</OmtTypography
              >
            </CFlex>

            <CFlex
              align-items="center"
              gap="29px"
            >
              <CBox>
                <OmtTypography
                  v-for="(obsLine, obsIndex) in obs.Obs"
                  :key="`obs-lin-${index}-${obsIndex}`"
                  as="p1"
                  color="var(--colors-light-primary-gray1)"
                >
                  {{ obsLine }}
                </OmtTypography>
              </CBox>
            </CFlex>
          </CBox>
        </template>

        <template v-else>
          <OmtTypography
            as="p1"
            color="var(--colors-light-primary-gray1)"
          >
            {{ refundDetails.obsProtocol }}
          </OmtTypography>
        </template>
      </CBox>
    </OmtRow>

    <OmtRow>
      <OmtTypography
        as="h2-bold"
        mb="24px"
        data-cy="refund-detail-docs-label"
      >
        {{ refundDetails.docsLabel }}
      </OmtTypography>
    </OmtRow>

    <OmtRow
      v-for="(refund, index) in refundDetails.refunds"
      :key="`stat-${index}`"
      mb="24px"
    >
      <RefundDetailCollapsableCard
        :refundDetail="refund"
        :cancelPendency="openCancellationModal"
        :isCollapsed="true"
        :complementaryPictures="pictures"
      >
        <CStack
          v-if="isAuditRefund(refund) && getFirstTicket(refund)"
          mt="24px"
          align-items="center"
        >
          <OmtButton
            data-cy="answer-audit-button"
            size="sm"
            :w="['100%', '326px']"
            @click="answerPendency({ ticket: getFirstTicket(refund), refundDetail: refund })"
          >
            Responder auditoria
          </OmtButton>

          <OmtButton
            variant="ghost"
            @click="openCancellationModal(getFirstTicket(refund))"
          >
            <OmtIcons
              font-size="16px"
              name="trash-can"
              mr="8px"
              size="inherit"
              color="var(--colors-light-support-error)"
            />
            <OmtTypography
              as="button-small"
              color="var(--colors-light-support-error)"
            >
              Cancelar solicitação
            </OmtTypography>
          </OmtButton>
        </CStack>
      </RefundDetailCollapsableCard>
    </OmtRow>

    <AuditFormCancellationModal
      :isOpen.sync="showCancellationModal"
      @cancel="handleCancel"
    />

    <OmtRow
      v-if="hasAuditRefund && pictures.length"
      class="audit-details__documents"
    >
      <section>
        <OmtTypography
          as="h2-bold"
          mb="8px"
          >Documentos complementares</OmtTypography
        >
        <OmtTypography
          as="p1"
          color="#8c8c8c"
          mb="14px"
          >Pedidos médicos e relatórios</OmtTypography
        >
        <div class="audit-details__pictures">
          <ImagePreviewer
            v-for="picture in pictures"
            :key="picture.id"
            v-bind="picture"
          />
        </div>
      </section>
    </OmtRow>

    <OmtRow>
      <AnswerPendencyModal
        :isOpen.sync="showPendencyModal"
        :pendencyToAnswer.sync="pendencyToAnswer"
        :protocol="protocolCreatedOnSave"
        :step="pendencyModalStep"
        :limitFiles="limitFiles"
        filesDescription="Insira somente a(s) imagem(ns) e/ou informações solicitadas e relacionadas a este(s) documento(s)"
        protocolMainTip="Você será notificado sobre o andamento desta solicitação pela home do APP, e pode acompanhar o status pela tela de Reembolsos."
        @savePendency="onSavePendency"
        @closeProtocolModal="onCloseProtocolModal"
      />
    </OmtRow>

    <FixedButtonRow v-if="pendencyContext && pendencyContext.refundDetail.status === REFUND_STATUS.PENDING">
      <OmtButton
        data-cy="answer-pendencies-button"
        :w="['100%', '308px !important']"
        height="40px"
        @click="handleFloatingPendencyClick"
      >
        <OmtTypography
          as="button-small"
          color="white"
        >
          Responder pendências
        </OmtTypography>
      </OmtButton>
    </FixedButtonRow>
  </OmtGrid>
</template>

<script>
import { computed, defineComponent, getCurrentInstance, onMounted, reactive, ref } from "vue";
import Swal from "sweetalert2/dist/sweetalert2.min.js";

import FixedButtonRow from "@/components/Atoms/FixedButtonRow/index.vue";
import AnswerPendencyModal from "@/components/Molecules/AnswerPendencyModal/index.vue";
import TextTile from "@/components/Molecules/TextTile";
import ImagePreviewer from "@/components/Molecules/ImagePreviewer";
import AuditFormCancellationModal from "@/components/Organisms/RefundNew/AuditForm/AuditFormCancellationModal.vue";
import RefundDetailCollapsableCard from "@/components/Organisms/RefundNew/RefundDetailCollapsableCard.vue";
import { REFUND_STATUS } from "@/constants/refundStatus";

function createRefundDetailsState() {
  return {
    protocol: "",
    createdAt: "",
    totalAmount: "",
    showValues: false,
    docsLabel: "",
    refunds: [],
    obsHistory: [],
    obsProtocol: "",
    accountDetails: {
      name: "",
      account: "",
      agency: "",
      bank: ""
    },
    pictures: []
  };
}

function collectDemonstrativoSources(demonstrativo) {
  if (Array.isArray(demonstrativo)) {
    return demonstrativo
      .map(item => item && item.source)
      .filter(source => typeof source === "string" && source.trim().length > 0);
  }

  if (demonstrativo && demonstrativo.source) {
    return [demonstrativo.source];
  }

  return [];
}

export default defineComponent({
  name: "Detail",
  components: {
    ImagePreviewer,
    AnswerPendencyModal,
    RefundDetailCollapsableCard,
    TextTile,
    AuditFormCancellationModal,
    FixedButtonRow
  },
  setup() {
    const instance = getCurrentInstance();
    const proxy = instance && instance.proxy ? instance.proxy : null;

    if (!proxy) {
      throw new Error("Refund detail page requires a Vue instance.");
    }

    const cancellationTicket = ref("");
    const limitFiles = ref(0);
    const showCancellationModal = ref(false);
    const showPendencyModal = ref(false);
    const pendencyToAnswer = ref({});
    const pendencyModalStep = ref(1);
    const protocolCreatedOnSave = ref("");
    const pictures = ref([]);
    const refundDetails = reactive(createRefundDetailsState());

    const protocol = computed(() => proxy.$route.params.protocolo || "");
    const hasAuditRefund = computed(() => refundDetails.refunds.some(isAuditRefund));

    function loadPicture(id) {
      return proxy.$store.dispatch("refund/fetchProtocolDetailImage", { documento: id, original: true });
    }

    async function loadPictures() {
      const list = Array.isArray(refundDetails.pictures) ? refundDetails.pictures : [];
      if (!list.length) {
        pictures.value = [];
        return;
      }

      const loadedPictures = [];

      for (const picture of list) {
        const source = await proxy.$store.dispatch("refund/fetchProtocolDetailImage", {
          documento: picture.id,
          original: false
        });

        loadedPictures.push({
          id: picture.id,
          source,
          loadModalImage: () => loadPicture(picture.id)
        });
      }

      pictures.value = loadedPictures;
    }

    const hasDemonstrativo = computed(() => {
      for (const refund of refundDetails.refunds || []) {
        for (const ticket of refund.list || []) {
          for (const doc of ticket.docs || []) {
            if (collectDemonstrativoSources(doc && doc.demonstrativo).length > 0) {
              return true;
            }
          }
        }
      }

      return false;
    });

    const isCreditReleased = computed(() => {
      const firstRefund = Array.isArray(refundDetails.refunds) ? refundDetails.refunds[0] : null;
      return (firstRefund && firstRefund.status) === REFUND_STATUS.CREDIT_RELEASED;
    });

    const pendencyContext = computed(() => {
      for (const refund of refundDetails.refunds || []) {
        if (![REFUND_STATUS.PENDING, REFUND_STATUS.IN_AUDIT].includes(refund.status)) {
          continue;
        }

        const firstTicket = getFirstTicket(refund);

        if (firstTicket) {
          return { ticket: firstTicket, refundDetail: refund };
        }
      }

      return null;
    });

    function getFirstTicket(refund) {
      const tickets = Array.isArray(refund && refund.list) ? refund.list : [];

      for (const ticket of tickets) {
        if (ticket) {
          return ticket;
        }
      }

      return null;
    }

    function isAuditRefund(refund) {
      return refund && refund.status === REFUND_STATUS.IN_AUDIT;
    }

    function init() {
      if (!protocol.value) {
        proxy.$router.push({ name: "refund.new.list" });
        return;
      }

      proxy.$root.$emit("show-loading");
      return proxy.$store
        .dispatch("refund/fetchProtocolDetail", { protocol: protocol.value })
        .then(details => {
          if (!details) {
            proxy.$router.push({ name: "refund.new.list" });
            return undefined;
          }

          const nextRefundDetails = { ...createRefundDetailsState(), ...details };
          Object.keys(nextRefundDetails).forEach(key => {
            refundDetails[key] = nextRefundDetails[key];
          });

          return proxy.$store.dispatch("refund/fetchMaxFiles");
        })
        .then(limit => {
          if (limit !== undefined) {
            limitFiles.value = limit;
          }
          return loadPictures();
        })
        .catch(() => {
          proxy.$router.push({ name: "refund.new.list" });
        })
        .finally(() => {
          proxy.$root.$emit("hide-loading");
        });
    }

    function openCancellationModal(payload) {
      cancellationTicket.value = payload.ticket;
      showCancellationModal.value = true;
    }

    function handleCancel() {
      proxy.$router.push({
        name: "refund.new.audit.term",
        query: proxy.$route.query,
        params: {
          scenario: 6,
          answers: [],
          refund: refundDetails,
          ticket: cancellationTicket.value,
          mustCancelProtocol: true
        }
      });
    }

    function extractDemonstrativoLinks() {
      const links = [];

      for (const refund of refundDetails.refunds || []) {
        for (const ticket of refund.list || []) {
          for (const doc of ticket.docs || []) {
            links.push(...collectDemonstrativoSources(doc && doc.demonstrativo));
          }
        }
      }

      return links;
    }

    function downloadDemonstrativo() {
      if (!hasDemonstrativo.value) {
        return;
      }

      const links = extractDemonstrativoLinks();
      if (links.length) {
        globalThis.window.location.href = links[0];
      }
    }

    function onSavePendency(data) {
      proxy.$root.$emit("show-loading");
      showPendencyModal.value = false;

      let newRefund = {};

      return proxy.$store
        .dispatch("refund/returnRefundTicket", data)
        .then(response => {
          newRefund = response || {};

          if (newRefund && newRefund.Data) {
            return proxy.$store
              .dispatch("saude/fetchProfileMessages")
              .then(() => proxy.$store.dispatch("refund/fetchProtocols", { protocol: newRefund.Data.protocoloANS }));
          }
        })
        .catch(error => {
          console.error(error && error.message ? error.message : error);
        })
        .finally(() => {
          proxy.$root.$emit("hide-loading");
        })
        .then(() => {
          if (newRefund && newRefund.Data) {
            protocolCreatedOnSave.value = newRefund.Data.protocoloANS;
            pendencyModalStep.value = 2;
            showPendencyModal.value = true;
            return;
          }

          return Swal.fire({
            title: "Erro",
            text: "Não foi possivel registrar o atendimento.\nServiço temporariamente indisponível.",
            confirmButtonText: "OK",
            icon: "error"
          });
        });
    }

    function onCloseProtocolModal() {
      proxy.$toast({
        description: "Sua resposta foi enviada para análise",
        status: "success",
        duration: 10000,
        position: "top"
      });

      proxy.$root.$emit("show-loading");
      proxy.$router.replace({ name: "refund.new.list" });
    }

    function answerPendency(context) {
      if (context.refundDetail.status === REFUND_STATUS.PENDING) {
        showPendencyModal.value = true;
        pendencyToAnswer.value = context.ticket;
        return;
      }

      if (context.refundDetail.status === REFUND_STATUS.IN_AUDIT) {
        proxy.$router.push({
          name: "refund.new.audit.explanation",
          query: proxy.$route.query,
          params: {
            dueDate: context.ticket.dueDate,
            ticket: context.ticket.ticket,
            refund: refundDetails
          }
        });
      }
    }

    function handleFloatingPendencyClick() {
      if (!pendencyContext.value) {
        return;
      }

      answerPendency(pendencyContext.value);
    }

    onMounted(init);

    return {
      hasAuditRefund,
      REFUND_STATUS,
      refundDetails,
      limitFiles,
      showCancellationModal,
      showPendencyModal,
      pendencyToAnswer,
      pendencyModalStep,
      protocolCreatedOnSave,
      pictures,
      hasDemonstrativo,
      isCreditReleased,
      pendencyContext,
      protocol,
      getFirstTicket,
      isAuditRefund,
      openCancellationModal,
      handleCancel,
      downloadDemonstrativo,
      onSavePendency,
      onCloseProtocolModal,
      answerPendency,
      handleFloatingPendencyClick
    };
  }
});
</script>

<style scoped>
.audit-details.omt-grid {
  padding-top: 16px;
  padding-bottom: 96px;
}
.audit-details ::v-deep h1 {
  margin-bottom: 20px;
  line-height: 32px;
  overflow-wrap: anywhere;
}
.audit-details ::v-deep h2 {
  line-height: 20px;
}
.audit-details ::v-deep p {
  line-height: 22px;
}
.audit-details ::v-deep [data-cy="text-tile"] > :first-child {
  line-height: 18px;
}
.audit-details .audit-details__summary {
  padding-bottom: 14px;
  border-bottom: 0.5px solid #dadada;
  margin-bottom: 16px;
}
.audit-details .audit-details__account {
  margin-bottom: 16px;
}
.audit-details .audit-details__account .refund-detail-section {
  padding-bottom: 14px;
}
.audit-details .audit-details__notes {
  margin-bottom: 20px;
}
.audit-details .audit-details__notes .refund-detail-section {
  padding-bottom: 0;
  border-bottom: 0;
}
.audit-details .audit-details__account-fields {
  gap: 14px 16px;
}
.audit-details__pictures {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 74px));
  gap: 16px;
}
.audit-details__pictures ::v-deep [data-cy="omt-image-previewer"] {
  width: 100%;
  height: auto;
  aspect-ratio: 1;
}
.audit-details__pictures ::v-deep img {
  height: auto !important;
  aspect-ratio: 1;
}
@media (min-width: 1024px) {
  .audit-details.omt-grid {
    max-width: 1104px;
  }
  .audit-details .audit-details__account-fields {
    gap: 24px;
  }
}
.refund-detail-section {
  width: 100%;
  padding-bottom: 24px;
  border-bottom: 0.5px solid var(--colors-light-secondary-divider);
}

::v-deep .swal2-container {
  padding: 0;
}

::v-deep .swal2-popup {
  height: 100vh;
  width: 100vw;
  display: flex;
}

::v-deep .swal2-image {
  height: 80vh;
  width: 100%;
  object-fit: fill;
}
</style>
